/**
 * Distributed Tracing & Session Management Utility
 *
 * Implements MNC-grade end-to-end user journey tracking:
 * 1. Session ID (X-Session-Id): Persists in sessionStorage for the duration of the user's
 *    browser session (across all page navigation and actions).
 * 2. Trace ID (X-Trace-Id / X-Correlation-Id): Generated uniquely per request/action,
 *    allowing millisecond-level correlation across Frontend -> Nginx -> Backend -> DB.
 */

const SESSION_KEY = "travaio_session_id";

export const generateUUID = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Returns the current session ID, or generates and stores a new one if not present.
 * Session ID persists across all clicks/pages until the browser tab is closed.
 * Format: Random UUID v4
 */
export const getSessionId = () => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return generateUUID();
  }

  try {
    let sessionId = window.sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = generateUUID();
      window.sessionStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  } catch (e) {
    return generateUUID();
  }
};

/**
 * Generates a unique trace ID for an individual HTTP request or user action.
 * Format: Random UUID v4
 */
export const createTraceId = () => {
  return generateUUID();
};

/**
 * Attaches tracing headers (X-Session-Id, X-Trace-Id, X-Correlation-Id, X-Client-Platform)
 * to an Axios request configuration object.
 */
export const attachTracingHeaders = (config) => {
  if (!config.headers) {
    config.headers = {};
  }

  // Guard: Do not attach custom tracing headers to external UPM requests
  const url = (config.url || "") + (config.baseURL || "");
  if (url.includes("upm")) {
    return config;
  }

  const sessionId = getSessionId();
  const traceId = createTraceId();

  config.headers["X-Session-Id"] = sessionId;
  config.headers["X-Trace-Id"] = traceId;
  config.headers["X-Correlation-Id"] = traceId;
  config.headers["X-Client-Platform"] = "web";

  // If userId exists in localStorage, attach for fast user-level filtering in Loki
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const userId = window.localStorage.getItem("userId");
      if (userId) {
        config.headers["X-User-Id"] = userId;
      }
    } catch (e) {
      // Ignore localStorage read errors
    }
  }

  return config;
};
